import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  LinearProgress,
  Stack
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Technology, TechnologyFormData } from "../../Graphql/types/technology";

interface Props {
  open: boolean;
  onClose: () => void;
  technology: Technology;
  onSave: (tech: Technology) => void;
  isUpdating?: boolean;
}

// Validation schema
const validationSchema: Yup.ObjectSchema<TechnologyFormData> =
  Yup.object().shape({
    title: Yup.string()
      .required("Title is required")
      .min(3, "Title must be at least 3 characters")
      .matches(/^[A-Za-z\s'-.]+$/, "Special chars are not allowed"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .matches(/^[A-Za-z\s'-.()]+$/, "Special chars are not allowed")
  });

const EditTechnologyDialog: React.FC<Props> = ({
  open,
  onClose,
  technology,
  onSave,
  isUpdating
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<TechnologyFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: technology
  });

  // save the technology
  const handleUpdateTech = (item: TechnologyFormData): void => {
    onSave({
      ...technology,
      ...item
    });
  };

  const isEmpty = Object.keys(technology).length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle textAlign="center">{`${isEmpty ? "Create" : "Edit"} Technology`}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Title"
          {...register("title")}
          error={!!errors.title}
          helperText={errors.title?.message}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Description"
          multiline
          minRows={4}
          {...register("description")}
          error={!!errors.description}
          helperText={errors.description?.message}
        />
      </DialogContent>
      {isUpdating && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit(handleUpdateTech)} color="primary">
          {`${isEmpty ? "Create" : "Save updates"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTechnologyDialog;
